import { useSyncExternalStore } from 'react'

/**
 * The result of the last run of the "Несоответствия" page, kept in localStorage so the sidebar can
 * show a badge WITHOUT loading the whole catalog on every console page (that would cost a full read
 * of materials, attribute values and rules on each visit). The badge is therefore "as of the last
 * check" and says so in its tooltip; opening the page refreshes it.
 */
export type LastCheck = { errors: number; warnings: number; at: number }

const STORAGE_KEY = 'estimatepro.inconsistencies.lastCheck'
const EVENT = 'estimatepro:inconsistencies'

function read(): LastCheck | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<LastCheck>
    if (typeof parsed.errors !== 'number' || typeof parsed.at !== 'number') return null
    return { errors: parsed.errors, warnings: parsed.warnings ?? 0, at: parsed.at }
  } catch {
    return null
  }
}

// useSyncExternalStore needs a stable snapshot: cache by the raw string.
let cachedRaw: string | null = null
let cachedValue: LastCheck | null = null

function snapshot(): LastCheck | null {
  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (raw !== cachedRaw) {
    cachedRaw = raw
    cachedValue = read()
  }
  return cachedValue
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('storage', onChange)
  window.addEventListener(EVENT, onChange)
  return () => {
    window.removeEventListener('storage', onChange)
    window.removeEventListener(EVENT, onChange)
  }
}

export function saveLastCheck(errors: number, warnings: number): void {
  const previous = snapshot()
  if (previous && previous.errors === errors && previous.warnings === warnings) return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ errors, warnings, at: Date.now() }))
  } catch {
    return
  }
  window.dispatchEvent(new Event(EVENT))
}

export function useLastCheck(): LastCheck | null {
  return useSyncExternalStore(subscribe, snapshot, () => null)
}
